import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import * as api from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';

/**
 * AuthCallback Component
 * Handles the OAuth callback from Emergent Google Auth.
 * Exchanges session_id for session_token and redirects to dashboard.
 * 
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { loginWithTokens } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          console.error('No session_id found in URL');
          navigate('/login', { replace: true });
          return;
        }

        // Exchange session_id for session
        const response = await api.exchangeGoogleSession(sessionId);
        
        if (response.data?.token) {
          // Same JWT + refresh token as email login → auto-renewal works for Google users too
          loginWithTokens(response.data);
          window.history.replaceState(null, '', window.location.pathname);
          toast.success('Login com Google realizado!');
          navigate('/dashboard', { replace: true });
        } else {
          throw new Error('Failed to authenticate');
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [navigate, loginWithTokens]);

  // Show minimal loading state during processing
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Autenticando...</p>
      </div>
    </div>
  );
}
